import { standardDeviation } from 'math-standard-deviation';

//For every section we want speed values, and combinations of colors and shapes:
/*
{ "speeds": [0, 1, 2, 3], "lanes": "3a, 2d, 1b, 4c" },
{ "speeds": [0, 3, 2, 1], "lanes": "1b, 4c, 2d, 3a", "action": "rulechange" }, 
*/
//Every section the combinations of shape and colors are shuffled. Neither shape or color should stay the same for all lanes.
//A rule changes means that a new color or shape becomes fastest. Configure per rule change whether this will be a shape or a color.
//  Or more precise: whether speed depends on color or shape
//When rule changes, the new 'winner' should be different from both color and shape of the fastest lane in the previous section.

const n = 29;//nr of sections to generate
const ruleChangeAt = [5, 10, 14, 20, 25];//at which sections does the rule change
const linkSpeedToColorPerRuleChange = [true, true, false, false, true, false];//for each rule, should speeds be linked to color (true) or shapes (false)
const speedMappingInitial = [1, 2, 3, 0];
const shapeIds = "abcd".split("");
const colorIds = "1234".split("");

const permutations = "0123,0132,0213,0231,0312,0321,1023,1032,1203,1230,1302,1320,2013,2031,2103,2130,2301,2310,3012,3021,3102,3120,3201,3210".split(",").map(str => str.split("").map(c => Number(c)));

/**
 * Generates sections (speeds and lanes) with all necessary constraints
 * Outputs to console. This can be pasted in game.json. 
 * NB: start and finish sections have to be added manually to game.json. Same for finish marker/action. So copy-paste carefully.
 * 
 * Also, standard deviation of counts of combinations of color+shape is logged.
 * Ideally this value should be around or below 1.
 */
export function generateLevel() {
    const linkSpeedToColor = linkSpeedToColorPerRuleChange.slice(0);
    //start with "1a, 2b, 3c, 4d"    
    let speedMapping = speedMappingInitial;
    let speedsMapsToColor = linkSpeedToColor.shift();

    let colors = permutations[0];
    let shapes = permutations[0];
    let speeds = calcSpeeds(speedsMapsToColor ? colors : shapes, speedMapping);

    let sections = [buildSectionString(colors, shapes, speedMapping, speedsMapsToColor, true)];
    const combiCount = {};

    for (let i = 1; i < n; i++) {
        //rule change?
        const ruleChange = ruleChangeAt.indexOf(i) > -1;
        if (ruleChange) {
            //find new speed mapping, either based on color or shape
            //Speed mapping should not result in same speeds continuing across section changes, 
            //and should be different from last speed mapping (at least for highest speed)
            speedsMapsToColor = linkSpeedToColor.shift();
            speedMapping = ArrayUtil.shuffle(permutations).find(speedMappingNew => {
                return speedMapping.every((value, idx) => value != speedMappingNew[idx]);
            });
        }
        //Keep attempting until we find a section that conforms to the constraints
        while (true) {
            //Make combinations of colors and shapes, so that all combinations are new, and order of both colors and shapes is different from previous colors and shapes
            //Start with color (this is arbitrary)
            const combisLast = combine(colors, shapes);
            const colorsNew = ArrayUtil.shuffle(permutations).find(perm => {
                //Ensure colors on lanes do not continue across section changes
                // (== new color list is different in each place from last section)
                if (hasMatchingValues(colors, perm)) {
                    return false;
                }
                //Ensure speeds of lanes do not continue across section changes
                // (== new speed list is different in each place from last section)
                if (speedsMapsToColor) {
                    const speedsNew = calcSpeeds(perm, speedMapping);
                    if (hasMatchingValues(speeds, speedsNew)) {
                        return false;
                    }
                }
                return true;
            });
            const shapesNew = ArrayUtil.shuffle(permutations).find(perm => {
                //Ensure shapes on lanes do not continue across section changes
                // (== new shape list is different in each place from last section)
                if (hasMatchingValues(shapes, perm)) {
                    return false;
                }
                //Ensure all combinations of color+shape are different from last section (e.g.: no red triangles in consecutive sections)
                const combis = combine(colorsNew, perm);
                if (combisLast.some(combi => combis.indexOf(combi) > -1)) {
                    return false;
                }
                //Ensure speeds of lanes do not continue across section changes
                // (== new speed list is different in each place from last section)
                if (!speedsMapsToColor) {
                    const speedsNew = calcSpeeds(perm, speedMapping);
                    if (hasMatchingValues(speeds, speedsNew)) {
                        return false;
                    }
                }
                return true;
            });
            if (shapesNew != null) {
                combine(colorsNew, shapesNew).forEach(combi => {
                    if (combiCount[combi] == null) combiCount[combi] = 0;
                    combiCount[combi]++;
                });
                colors = colorsNew;
                shapes = shapesNew;
                speeds = calcSpeeds(speedsMapsToColor ? colors : shapes, speedMapping);
                sections.push(buildSectionString(colors, shapes, speeds, ruleChange));
                break;
            }
        }
    }
    console.log(sections.join(",\n"));
    console.log("Combination counts: ", combiCount);
    console.log("Standard deviation: " + standardDeviation(Object.values(combiCount)));
}

/**
 * @param {Array<number>} colorIdxs 
 * @param {Array<number>} shapeIdxs 
 * @param {Array<number>} speedMapping 
 * @param {boolean} speedsMapsToColor 
 * @param {boolean} ruleChange 
 * @returns {string}
 */
const buildSectionString = function(colorIdxs, shapeIdxs, speeds, ruleChange) {
    const lanes = combine(colorIdxs, shapeIdxs);
    return `{ "speeds": [${speeds.join(", ")}], "lanes": "${lanes}"${ruleChange ? ', "action": "rulechange"' : ""} }`;
}

const hasMatchingValues = function(arr1, arr2) {
    return arr1.some((value, idx) => value == arr2[idx]);
}

const calcSpeeds = function(features, speedMapping) {
    return features.map(feature => speedMapping[feature]);
}

const combine = function(colors, shapes) {
    return colors.map((color, idx) => colorIds[color] + shapeIds[shapes[idx]]);
}

const ArrayUtil = {};
ArrayUtil.shuffle = function(a) {
    let j, x, i;
    for (i = a.length; i; i--) {
        j = Math.floor(Math.random() * i);
        x = a[i - 1];
        a[i - 1] = a[j];
        a[j] = x;
    }
    return a;
}